/**
 * Couleurs de la v2 : lecture, mélange et contraste WCAG 2.x.
 *
 * Fonctions pures, sans effet de bord au chargement du module (règle du code v2 :
 * docs/ux-v2/SPEC.md § 6.2). Serviront aussi aux couleurs des autres marques du planning
 * multi-marques (SPEC PL5 : texte coloré gardé lisible sur blanc).
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
  /** Opacité 0..1. */
  a: number;
}

export const WHITE = '#ffffff';
export const BLACK = '#000000';

/** Seuil AA pour un texte courant (WCAG 2.x, 1.4.3). */
export const AA = 4.5;

const clamp255 = (n: number) => Math.min(255, Math.max(0, Math.round(n)));

/**
 * Lit une couleur CSS simple : #rgb, #rgba, #rrggbb, #rrggbbaa, rgb() / rgba() (virgules ou
 * syntaxe à espaces, alpha en nombre ou en %). Renvoie null pour tout le reste (nom de couleur,
 * var(), hsl(), oklch()…) : l'appelant passe alors à la source suivante.
 */
export function parseColor(input: string | null | undefined): Rgb | null {
  if (typeof input !== 'string') return null;
  const value = input.trim().toLowerCase();
  if (value === '') return null;

  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(value);
  if (hex) {
    let h = hex[1];
    if (h.length <= 4) h = h.split('').map((c) => c + c).join('');
    const n = (i: number) => parseInt(h.slice(i, i + 2), 16);
    return { r: n(0), g: n(2), b: n(4), a: h.length === 8 ? n(6) / 255 : 1 };
  }

  const fn = /^rgba?\((.*)\)$/.exec(value);
  if (!fn) return null;
  const parts = fn[1]
    .replace(/\s*\/\s*/, ',')
    .split(/[\s,]+/)
    .filter(Boolean);
  if (parts.length < 3 || parts.length > 4) return null;

  const channel = (p: string) => (p.endsWith('%') ? (parseFloat(p) * 255) / 100 : parseFloat(p));
  const [r, g, b] = parts.slice(0, 3).map(channel);
  const a = parts[3] === undefined ? 1 : parts[3].endsWith('%') ? parseFloat(parts[3]) / 100 : parseFloat(parts[3]);
  if ([r, g, b, a].some((n) => Number.isNaN(n))) return null;

  return { r: clamp255(r), g: clamp255(g), b: clamp255(b), a: Math.min(1, Math.max(0, a)) };
}

export function isColor(input: string | null | undefined): boolean {
  return parseColor(input) !== null;
}

/** `#rrggbb` (l'opacité est ignorée). */
export function toHex(color: Rgb): string {
  return `#${[color.r, color.g, color.b].map((n) => clamp255(n).toString(16).padStart(2, '0')).join('')}`;
}

/** Normalise une couleur lisible en `#rrggbb` ; null sinon. */
export function normalizeHex(input: string | null | undefined): string | null {
  const color = parseColor(input);
  return color ? toHex(color) : null;
}

/** Teinte (0–360°), saturation et luminosité (0–1) HSL d'une couleur ; null si illisible. */
export function hslOf(input: string): { h: number; s: number; l: number } | null {
  const color = parseColor(input);
  if (!color) return null;
  const r = color.r / 255;
  const g = color.g / 255;
  const b = color.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

/** Écart de teinte (0–180°) entre deux couleurs ; Infinity si l'une est illisible ou grise. */
export function hueDistance(a: string, b: string): number {
  const x = hslOf(a);
  const y = hslOf(b);
  if (!x || !y || x.s === 0 || y.s === 0) return Number.POSITIVE_INFINITY;
  const d = Math.abs(x.h - y.h) % 360;
  return d > 180 ? 360 - d : d;
}

/** Luminance relative WCAG 2.x (0 = noir, 1 = blanc). */
export function relativeLuminance(color: Rgb): number {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(color.r) + 0.7152 * lin(color.g) + 0.0722 * lin(color.b);
}

/** Ratio de contraste WCAG entre deux couleurs opaques (1 à 21). */
export function contrastRatio(a: string, b: string): number {
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return 1;
  const la = relativeLuminance(ca);
  const lb = relativeLuminance(cb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Mélange `weight` (0..1) de `a` dans `b` : mix(rouge, blanc, 0.1) = teinte claire de rouge. */
export function mix(a: string, b: string, weight: number): string {
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return a;
  const w = Math.min(1, Math.max(0, weight));
  return toHex({
    r: ca.r * w + cb.r * (1 - w),
    g: ca.g * w + cb.g * (1 - w),
    b: ca.b * w + cb.b * (1 - w),
    a: 1,
  });
}

/**
 * Couleur de texte à poser sur `background` : celle des deux candidates (encre, blanc) qui
 * offre le meilleur contraste. Contrairement à un seuil de luminance fixe, ce choix donne
 * toujours le maximum atteignable (ex. orange #FF4B10 : encre 5,8:1 plutôt que blanc 3,35:1).
 */
export function readableTextOn(background: string, dark: string, light: string = WHITE): string {
  return contrastRatio(dark, background) >= contrastRatio(light, background) ? dark : light;
}

/**
 * `foreground` assombri (fond clair) ou éclairci (fond sombre) par petits pas jusqu'à atteindre
 * `ratio` contre `background`. Couleur déjà conforme : renvoyée telle quelle (normalisée).
 */
export function ensureContrast(foreground: string, background: string, ratio: number = AA): string {
  const fg = normalizeHex(foreground);
  const bg = normalizeHex(background);
  if (!fg || !bg) return foreground;
  if (contrastRatio(fg, bg) >= ratio) return fg;

  const bgIsLight = contrastRatio(BLACK, bg) >= contrastRatio(WHITE, bg);
  const target = bgIsLight ? BLACK : WHITE;
  for (let step = 1; step <= 100; step++) {
    const candidate = mix(target, fg, step / 100);
    if (contrastRatio(candidate, bg) >= ratio) return candidate;
  }
  return target;
}

/**
 * Seuil à partir duquel une couleur « porte » le blanc : 3:1, le minimum AA des grands textes. Au-delà, c'est une
 * couleur moyenne ou foncée, et les trois marques y posent du blanc (relevé du 04/10 sur dynamo-cycling.com,
 * riise-studios.com et punch-studios.com : boutons « Réserver » blancs sur #FF2021, #7D301C, #FF5717 et #263FFF).
 * En deçà (jaune, citron, crème, pastels), elles posent de l'encre (thème monday-2026 : --m-on-accent-page).
 */
export const WHITE_CARRIER = 3;

/** Texte posé sur une couleur : surface à peindre (la couleur, ou la même teinte foncée au minimum) et son texte. */
export interface TextOnColor {
  surface: string;
  text: string;
  /** Contraste final texte / surface. */
  ratio: number;
}

/**
 * `surface` foncée (mélange de noir : même teinte) au minimum pour que `text` y atteigne `ratio`. Recherche fine (pas
 * de 0,1 %) : on fonce juste assez, pas d'un cran de 1 % de trop. Surface déjà conforme : renvoyée telle quelle.
 */
export function darkenToContrast(surface: string, text: string, ratio: number = AA): string {
  const bg = normalizeHex(surface);
  const fg = normalizeHex(text);
  if (!bg || !fg) return surface;
  if (contrastRatio(fg, bg) >= ratio) return bg;
  if (contrastRatio(fg, BLACK) < ratio) return BLACK;
  // Le contraste croît avec la part de noir (texte clair) : recherche dichotomique de la plus petite part suffisante.
  let low = 0;
  let high = 1;
  for (let i = 0; i < 20; i++) {
    const middle = (low + high) / 2;
    if (contrastRatio(fg, mix(BLACK, bg, middle)) >= ratio) high = middle;
    else low = middle;
  }
  // Arrondi des canaux à l'entier : on avance par pas de 0,1 % jusqu'à un résultat réellement conforme.
  for (let weight = high; weight <= 1; weight += 0.001) {
    const candidate = mix(BLACK, bg, weight);
    if (contrastRatio(fg, candidate) >= ratio) return candidate;
  }
  return BLACK;
}

/**
 * Règle de la v2 pour tout texte posé sur une couleur (boutons, spot choisi, étiquettes, bandeaux) : la convention
 * des marques, garantie AA.
 * - Couleur qui porte le blanc (≥ 3:1, cf. WHITE_CARRIER) : texte BLANC, comme sur les sites des trois marques ; si
 *   le blanc n'atteint pas 4,5:1, la surface est foncée au minimum dans sa teinte (#FF2021 → #E91D1E, #FF5717 →
 *   #D24713) plutôt que de passer le texte à l'encre.
 * - Couleur claire : texte à l'encre (jamais de blanc sur un jaune) ; surface inchangée, sauf encre trop claire.
 */
export function textOnColor(color: string, ink: string, light: string = WHITE, ratio: number = AA): TextOnColor {
  const surface = normalizeHex(color);
  if (!surface) return { surface: color, text: light, ratio: 1 };
  if (contrastRatio(light, surface) >= WHITE_CARRIER) {
    const darker = darkenToContrast(surface, light, ratio);
    return { surface: darker, text: normalizeHex(light) ?? light, ratio: contrastRatio(light, darker) };
  }
  const text = normalizeHex(ink) ?? ink;
  const readable = contrastRatio(text, surface) >= ratio ? surface : ensureContrast(surface, text, ratio);
  return { surface: readable, text, ratio: contrastRatio(text, readable) };
}
